<template>
  <div style="width: 100%;">
    <el-col :span="24" id="trans_posting" v-loading.body="changingPage" element-loading-text="Loading...">
      <el-row type="flex" justify="start" align="middle" id="trans_posting-header">
        <HeaderInfoText
          :colspan="12"
          :title="pageTitle"
          :meta="{ count: transactions.length, shown: trxPageData.length }"
          :selection="selectedTrxs"
        />
        <el-col :span="6">
          <el-row type="flex" justify="center" v-if="batches.length > 20">
            <el-pagination
              layout="prev, pager, next, jumper"
              :total="batches.length"
              :page-size="20"
              :current-page.sync="currentPage"
            />
          </el-row>
        </el-col>
        <el-col :span="8" class="h-padded right-aligned-col">
          <el-button
            type="primary"
            :disabled="!selectedTrxs.length"
            :loading="receivingReports"
            @click="handleAuditBtnClick">
            Audit Selected
          </el-button>
        </el-col>
      </el-row>
      <el-row type="flex" justify="start" align="middle" style="padding: 12px;">
        <el-col :span="3" style="margin-right: 24px;">
          <el-row type="flex" align="middle" justify="start">
            <el-input
              :disabled="shouldDisableFilters"
              placeholder="Keyword search"
              :icon="filters.search ? 'circle-cross' : 'search'"
              :on-icon-click="() => (filters.search = '')"
              ref="trx_search_filter"
              v-model="filters.search"
            />
          </el-row>
        </el-col>
        <el-col :span="3" style="margin-right: 24px;">
          <el-row type="flex" align="middle" justify="start">
            <el-select
              v-model="filters.batch__in"
              filterable
              :multiple="true"
              placeholder="Batch"
              style="width: 100%;"
              :disabled="shouldDisableFilters">
              <el-option v-for="opt in originalBatches" :key="opt.id" :label="opt.name" :value="opt.id" />
            </el-select>
          </el-row>
        </el-col>
        <el-col :span="3" style="margin-right: 24px;">
          <el-row type="flex" align="middle" justify="start">
            <el-button @click="handleFilters" :disabled="shouldDisableFilters" :loading="filtering">
              {{ !filtersHaveChanged && !filtersAreEmpty ? 'Reset' : 'Apply' }}
            </el-button>
          </el-row>
        </el-col>
      </el-row>
      <el-row type="flex" justify="center" align="middle">
        <el-col v-if="filtering">
          <el-row type="flex" justify="center" align="middle" :style="`height: ${tableHeight}px;`">
            <i class="el-icon-loading" style="font-size: 18px; color: dodgerblue" />
            <span style="font-size: 18px; color: dodgerblue; margin: 12px;">
              {{ filtersHaveChanged ? 'Applying' : 'Resetting' }} filters, please wait...
            </span>
          </el-row>
        </el-col>
        <el-col v-else-if="!dataLoaded || (!filtering && !batches.length)">
          <el-row type="flex" justify="center" align="middle" :style="`height: ${tableHeight}px;`">
            <i :class="`el-icon-${!dataLoaded ? 'loading' : 'close'}`" style="font-size: 18px; color: dodgerblue" />
            <span style="font-size: 18px; color: dodgerblue; margin: 12px;">
              {{ !dataLoaded ? 'Loading batches and related transactions, please wait...' : 'No postable transactions to show' }}
            </span>
          </el-row>
        </el-col>
        <el-table
          v-else-if="!filtering && batches.length"
          border
          style="width: 100%; z-index: 0;"
          ref="postingTable"
          :data="batchPageData"
          :maxHeight="tableHeight"
          @expand="handleRowExpansion"
          @select="handleBatchSelectionChange"
          @select-all="handleBatchSelectAllChange">
          <el-table-column type="selection" :selectable="batchSelectable" width="55" align="center" />
          <el-table-column type="expand">
            <template slot-scope="scope">
              <table class="trx-batch-table" style="width: 100%;">
                <colgroup>
                  <col>
                  <col>
                  <col>
                  <col>
                  <col>
                  <col>
                  <col>
                  <col>
                  <col>
                  <col class="trx-action-buttons">
                </colgroup>
                <tr>
                  <th style="width: 20px;" />
                  <th style="width: 60px;">
                    <div>Ledger</div>
                  </th>
                  <th style="width: 160px;">
                    <div>Number</div>
                  </th>
                  <th style="width: 110px;">
                    <div>Date</div>
                  </th>
                  <th style="width: 160px;">
                    <div>Transaction Type</div>
                  </th>
                  <th style="width: 300px;">
                    <div>Description</div>
                  </th>
                  <th style="width: 180px;">
                    <div>Vendor</div>
                  </th>
                  <th style="width: 180px;">
                    <div>Bank</div>
                  </th>
                  <th>
                    <div>Debits</div>
                  </th>
                  <th class="header-centered">
                    <div>Status</div>
                  </th>
                </tr>
                <tr v-for="trx in expandedBatchTransactions[scope.row.id]" :id="`trx-${trx.ledger}`">
                  <td>
                    <div class="trx-cell-data" style="text-align: center;">
                      <span>
                         <input
                           class="el-checkbox__inner"
                           type="checkbox"
                           :disabled="!transactionPermissions(trx)"
                           :value="selectedTrxs.includes(trx.ledger)"
                           :checked="selectedTrxs.includes(trx.ledger)"
                           @input="(e) => handleTrxSelectionChange(e, trx.ledger)"
                         />
                      </span>
                    </div>
                  </td>
                  <td>
                    <div class="trx-cell-data">
                      {{ trx.ledger }}
                    </div>
                  </td>
                  <td style="width: 160px;">
                    <div @click="openTrxInNewTab(trx)" class="trx-cell-data clickable-trx-row" v-if="transactionPermissions(trx)">
                      {{ trx.number }}
                    </div>
                    <div class="trx-cell-data" v-else>
                      {{ trx.number }}
                    </div>
                  </td>
                  <td style="width: 122px;">
                    <div @click="openTrxInNewTab(trx)" class="trx-cell-data clickable-trx-row" v-if="transactionPermissions(trx)">
                      {{ trx.date }}
                    </div>
                    <div class="trx-cell-data" v-else>
                      {{ trx.date }}
                    </div>
                  </td>
                  <td style="width: 160px;">
                    <div class="trx-cell-data">
                      {{ (trx.source_code && Number(trx.source_code) === 5) ? 'Payroll' : trx.transactionType }}
                    </div>
                  </td>
                  <td style="width: 300px;">
                    <div class="trx-cell-data">
                      {{ trx.description }}
                    </div>
                  </td>
                  <td style="width: 180px;">
                    <div class="trx-cell-data">
                      {{ getVendorById(trx.vendor) && getVendorById(trx.vendor).name ? getVendorById(trx.vendor).name : '' }}
                    </div>
                  </td>
                  <td style="width: 180px;">
                    <div class="trx-cell-data">
                      {{ getBankById(scope.row.bank) && getBankById(scope.row.bank).name ? getBankById(scope.row.bank).name : '' }}
                    </div>
                  </td>
                  <td>
                    <div class="trx-cell-data">
                      {{ formatAmount(trx[trx.transactionType === 'Journal Entry' ? 'debits_decimal' : 'amount_decimal']) }}
                    </div>
                  </td>
                  <td>
                    <el-row type="flex" justify="center">
                      <el-button-group>
                        <el-button size="small" plain disabled>
                          {{ trx.status }}
                        </el-button>
                        <el-button
                          v-if="['OPEN', 'AUDITED'].includes(trx.status)"
                          type="info"
                          size="small"
                          :disabled="(auditing && auditing.includes(trx.ledger)) || (!transactionPermissions(trx))"
                          :loading="auditing && auditing.includes(trx.ledger)"
                          @click="handleAuditBtnClick(trx)">
                          {{ auditing && auditing.includes(trx.ledger) ? '' : 'Audit' }}
                        </el-button>
                      </el-button-group>
                    </el-row>
                  </td>
                </tr>
              </table>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="Batch Name" align="start" fit />
        </el-table>
      </el-row>
    </el-col>
  </div>
</template>

<script>
  /* eslint-disable no-unused-vars */
  import { mapGetters } from 'vuex'
  import Filters from '@/components/Filters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import SelectFilter from '@/components/FilterComponents/SelectFilter'
  import AutocompleteFilter from '@/components/FilterComponents/AutocompleteFilter'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import getTransactionReportParams from '@/utils/helpers/GetTransactionReportParams'
  import { notify } from '@/utils/helpers/Notify'
  import { formatLocale } from '@/utils/helpers/StringsNumbers'
  import { payload as bankActionPayload } from '@/components/RouteComponents/Banks/actionPayload'
  import { payload as batchActionPayload } from '@/components/RouteComponents/Batches/actionPayload'
  import { payload as vendorActionPayload } from '@/components/RouteComponents/Vendors/actionPayload'
  import Mousetrap from 'mousetrap'

  export default {
    components: {
      Filters,
      KeywordFilter,
      SelectFilter,
      AutocompleteFilter,
      HeaderInfoText
    },
    data () {
      return {
        dataLoaded: false,
        previousFilters: null,
        filters: { search: '', batch__in: [] },
        filtering: false,
        expandedBatches: [],
        tableHeight: 1000,
        currentPage: 1,
        changingPage: false,
        auditing: null,
        posting: null,
        selectedBatches: [],
        selectedTrxs: [],
        originalBatches: []
      }
    },
    methods: {
      handleFilters () {
        if (this.filtersHaveChanged) {
          this.loadData(true)
        } else {
          const filterInitState = { search: '', batch__in: [] }
          this.filters = { ...filterInitState }
          this.loadData(true)
        }
      },
      /**
       * Event handler for when user expands/collapses a batch row
       * @param row         The batch row being expanded/collapsed
       * @param expanded    Whether the row is collapsed
       */
      handleRowExpansion (row, expanded) {
        if (expanded) {
          this.expandedBatches.push(row.id)
        } else {
          this.expandedBatches.splice(this.expandedBatches.indexOf(row.id), 1)
        }
      },
      /**
       * Adds one or more batches to the current batch selection array
       * @param selection
       */
      addBatchesToSelection (selection) {
        selection.map(selectedBatch => selectedBatch.id).forEach(selectedBatchId => {
          const isInCurrentPage = !!this.batchPageData.find(batch => batch.id === selectedBatchId)
          const isAlreadySelected = this.selectedBatches.includes(selectedBatchId)
          if (isInCurrentPage && !isAlreadySelected) {
            this.selectedBatches.push(selectedBatchId)
          }
        })
      },
      /**
       * Handles batch-level transaction selection for individual batches
       * @param selection
       * @param batch
       */
      handleBatchSelectionChange (selection = [], batch) {
        if (selection.find(b => b.id === batch.id)) { // Batch was selected: check all transactions related to this batch
          this.addBatchesToSelection(selection)
          let batchTrxs = []
          selection.forEach(selectedBatch => {
            const transactionsInBatch = this.transactions.filter(trx => trx.batch === selectedBatch.id && this.transactionPermissions(trx)).map(trx => trx.ledger)
            batchTrxs = [...batchTrxs, ...transactionsInBatch]
          })
          this.selectedTrxs = [...new Set([...this.selectedTrxs, ...batchTrxs])]
        } else { // Batch was deselected: uncheck all transactions related to this batch
          this.selectedBatches.splice(this.selectedBatches.indexOf(batch.id), 1)
          const selectedTrxs = this.selectedTrxs.map(ledgerId => this.transactions.find(trx => trx.ledger === ledgerId))
          this.selectedTrxs = selectedTrxs.filter(trx => batch.id !== trx.batch).map(trx => trx.ledger)
        }
        this.$store.dispatch('updateTransactionSelection', this.selectedTrxs.map(ledgerId => {
          return this.transactions.find(trx => trx.ledger === ledgerId)
        }))
      },
      /**
       * Handles batch-level transaction selection when "select all" checkbox is used
       * @param selection
       */
      handleBatchSelectAllChange (selection = []) {
        let batchTrxs = []
        if (selection.length) { // All in page selected
          this.addBatchesToSelection(selection)   // Add this page's batches to the current selection
          this.batchPageData.forEach(selectedBatch => { // Add all transactions from this page's batches to the current transaction selection
            const transactionsInBatch = this.transactions
              .filter(trx => trx.status !== 'POISTED' && trx.batch === selectedBatch.id && this.transactionPermissions(trx))
              .map(trx => trx.ledger)
            batchTrxs = [...batchTrxs, ...transactionsInBatch]
          })
          this.selectedTrxs = [...new Set([...this.selectedTrxs, ...batchTrxs])]
        } else { // None in page selected
          this.batchPageData.forEach(deselectedBatch => { // Iterate through all batches in the current page
            this.selectedBatches.splice(this.selectedBatches.indexOf(deselectedBatch.id), 1) // Remove deselected batch from this.selectedBatches
            const transactionsInBatch = this.transactions.filter(trx => trx.batch === deselectedBatch.id).map(trx => trx.ledger) // For each batch, get all transactions in this batch
            this.selectedTrxs = this.selectedTrxs.filter(ledgerId => !transactionsInBatch.includes(ledgerId)) // Iterate through all selected transactions and filter out any that are in transactionsInBatch
          })
        }
        this.$store.dispatch('updateTransactionSelection', this.selectedTrxs.map(ledgerId => {
          return this.transactions.find(trx => trx.ledger === ledgerId)
        }))
      },
      /**
       * Handles transaction-level transaction selection when "select all" checkbox is used
       * @param e
       * @param trxLedgerId
       */
      handleTrxSelectionChange (e, trxLedgerId) {
        if (this.selectedTrxs.includes(trxLedgerId)) {
          this.selectedTrxs = this.selectedTrxs.filter(ledgerId => trxLedgerId !== ledgerId)
        } else {
          this.selectedTrxs.push(trxLedgerId)
        }
        const trx = this.trxPageData.find(trx => trx.ledger === trxLedgerId)
        const batch = this.batchPageData.find(batch => batch.id === trx.batch)
        const numberOfSelectedTrxsInBatch = this.selectedTrxBatches.filter(batchId => batchId === batch.id).length
        let table = this.$refs.postingTable
        if (numberOfSelectedTrxsInBatch > 0) { // add batch to selection
          this.addBatchesToSelection([batch])
        } else { // remove batch from batch selection
          this.selectedBatches.splice(this.selectedBatches.indexOf(batch.id), 1)
        }
        table.toggleRowSelection(batch, !!this.selectedBatches.find(batchId => batchId === batch.id))
        this.$store.dispatch('updateTransactionSelection', this.selectedTrxs.map(ledgerId => {
          return this.transactions.find(trx => trx.ledger === ledgerId)
        }))
      },
      /**
       * Preserves the selected transactions and shows them as selected whenever the table is re-rendered
       * due to a page or tab change
       */
      restoreSelection () {
        let table = this.$refs.postingTable
        if (table) {
          table.$nextTick(() => {
            this.batchPageData.forEach(batch => {
              if (this.selectedBatches.includes(batch.id)) {
                table.toggleRowSelection(batch, true)
              }
            })
          })
        }
      },
      /**
       * Handles auditing a transaction. The transaction can then be posted from the report viewer.
       * @param trx   Transaction to be audited. If provided, audits a single transaction rather than the selection
       */
      async handleAuditBtnClick (trx = null) {
        const transactions = trx && trx.id ? [trx] : this.selectedTransactions.filter(v => ['OPEN', 'AUDITED'].includes(v.status))
        if (trx && trx.id) {
          await this.$store.dispatch('updateFilteredTransactionSelection', [trx])
        }
        if (!(transactions && transactions.length)) {
          notify(this, 'warning', 'No transactions selected')
          return
        }
        this.auditing = transactions.map(v => v.ledger)
        const data = getTransactionReportParams(this, transactions)
        try {
          const resp = await this.$store.dispatch('cacheParams', { data }) // Render report, then send updated status (PUT)
          if (!resp || !resp.hasOwnProperty('id') || !resp.id) {
            throw new Error()
          }
          const payload = { query: { cached_parameters: resp.id }, type: 'audit/transaction', reportId: resp.reportId }
          await this.$store.dispatch('startRenderingReport', payload)
        } catch (e) {
          console.log(e)
          notify(this, 'error', 'Error generating audit report')
        } finally {
          this.auditing = null
        }
      },
      /**
       * Assigns a class name to a given row, which allows us to highlight the row yellow
       * when a transaction has been audited, and green when a transaction has been posted.
       * @param trxLedgerId
       * @return {*}
       */
      setAuditedRowClassName (trxLedgerId) {
        const trx = this.transactions.find(trx => trx.ledger === trxLedgerId)
        if (trx && trx.status === 'AUDITED') {
          const trxRow = document.getElementById(`trx-${trx.id}`)
          if (trxRow) {
            trxRow.classList.add('audited-row')
          }
        }
      },
      /**
       * Formats dollar amounts with thousands separators
       * @param value
       * @return {*}
       */
      formatAmount (value) {
        return value ? formatLocale(value) : ''
      },
      /**
       * Opens a transaction in a new tab
       * @param trx
       */
      openTrxInNewTab (trx) {
        let url = ''
        switch (trx.transactionType) {
          case 'Journal Entry':
            url = `/jes/${trx.id}`
            if (trx.source_code && Number(trx.source_code === 5)) {
              url = `/prs/${trx.id}`
            }
            break
          case 'Accounts Payable':
            url = `/aps/${trx.id}`
            break
          case 'Petty Cash':
            url = `/pettycash/${trx.id}`
        }
        const win = window.open(url, '_blank')
        win.focus()
      },
      /**
       * Bind filter application (search) to enter key using Mousetrap.js
       */
      bindMousetrap () {
        if (this.$refs && this.$refs[`trx_search_filter`] && this.$refs['trx_search_filter'].$refs.input) {
          this.mousetrap = new Mousetrap(this.$refs['trx_search_filter'].$refs.input)
          this.mousetrap.bind('enter', () => this.loadData(true))
        }
      },
      /**
       * Clears cached transaction data from the store
       */
      async clearTransactionsFromStore () {
        const promises = [
          this.$store.dispatch('clearApState', true),
          this.$store.dispatch('resetApMeta'),
          this.$store.dispatch('clearJeState', true),
          this.$store.dispatch('resetJeMeta'),
          this.$store.dispatch('clearPcState', true),
          this.$store.dispatch('resetPcMeta')
        ]
        await Promise.all(promises)
      },
      /**
       * Loads postable transactions and related dependencies
       */
      async loadData (filtering = false) {
        this.dataLoaded = false
        this.filtering = filtering
        try {
          await this.clearTransactionsFromStore()
          if (!filtering) {
            await this.$store.dispatch('fetchUiSetting', { name: 'CONFIRM_ON_POST' })
          }
          const pageSize = 50
          let additionalPageRequests = []
          const generateRequest = (type, page = null) => {
            let query = { status__in: ['OPEN', 'AUDITED'], page_size: pageSize }
            if (filtering && this.filtersHaveChanged) {
              for (let f in this.filters) {
                if (this.filters[f].length) {
                  query = { ...query, [f]: this.filters[f] }
                }
              }
            }
            if (page) {
              query.page = page
            }
            return () => this.$store.dispatch(`fetch${type}WithQuery`, query)
          }
          const getAdditionalPageRequestsNeeded = (count, firstPageLength) => Math.ceil(count / firstPageLength)
          const loadAdditionalPageRequests = (type, resp) => {
            if (resp && resp.count > pageSize) {
              let i = 2
              while (i <= getAdditionalPageRequestsNeeded(resp.count, pageSize)) {
                additionalPageRequests.push(generateRequest(type, i)())
                i++
              }
            }
          }
          if (this.$route.query && this.$route.query.type) {
            const resp = await generateRequest(this.$route.query.type)()
            loadAdditionalPageRequests(this.$route.query.type, resp)
          } else {
            const jesResp = await generateRequest('Jes')()
            const apsResp = await generateRequest('Aps')()
            const pcsResp = await generateRequest('Pcs')()
            loadAdditionalPageRequests('Jes', jesResp)
            loadAdditionalPageRequests('Aps', apsResp)
            loadAdditionalPageRequests('Pcs', pcsResp)
          }
          await Promise.all(additionalPageRequests)
          let transactions
          if (this.$route.query && this.$route.query.type) {
            transactions = this.$store.getters[`get${this.$route.query.type}Cache`] || []
          } else {
            const aps = this.$store.getters.getApsCache || []
            const jes = this.$store.getters.getJesCache || []
            const pcs = this.$store.getters.getPcsCache || []
            transactions = [...aps, ...jes, ...pcs]
          }
          const batches = [...new Set([...transactions.map(trx => trx.batch)])] // fetch dependencies (batch, vendor, bank should already be cached)
          const vendors = [...new Set(transactions.filter(trx => trx.vendor).map(trx => trx.vendor))]
          let dependencies = []
          if (!filtering && batches.length) {
            dependencies.push(this.$store.dispatch('fetchWithQuery', { ...batchActionPayload, query: { id__in: batches, page_size: 200 } }))
          }
          if (vendors.length) {
            dependencies.push(this.$store.dispatch('fetchWithQuery', { ...vendorActionPayload, query: { id__in: vendors, page_size: 200 } }))
          }
          await Promise.all(dependencies)
          if (!filtering) {
            const banks = [...new Set([...batches.map(batch => this.$store.getters.getBatchById(batch).bank)])] // fetch dependencies (batch, vendor, bank should already be cached)
            await this.$store.dispatch('fetchWithQuery', { ...bankActionPayload, query: { id__in: banks, page_size: 200 } })
          }
          this.dataLoaded = true
        } catch (e) {
          console.log(e)
        } finally {
          this.filtering = false
          if (filtering) {
            this.previousFilters = JSON.stringify(this.filters)
            setTimeout(() => this.restoreSelection())
          }
        }
      },
      /**
       * Resets certain component data properties back to their original state
       */
      resetComponentState () {
        this.originalBatches = this.batches
        this.expandedBatches = []
        this.currentPage = 1
        this.selectedBatches = []
        this.selectedTrxs = []
        this.$store.dispatch('updateTransactionSelection', [])
      },
      resetFilterState () {
        this.filters = { search: '', batch__in: [] }
        this.previousFilters = { ...this.filters }
      },
      async reloadComponent () {
        this.originalBatches = []
        this.resetFilterState()
        await this.loadData()
        this.resetComponentState()
      },
      transactionPermissions (trx) {
        let type = trx.transactionType
        if (trx.source_code && Number(trx.source_code) === 5) {
          type = 'Payroll'
        }
        let transactionTypes = {
          'Accounts Payable': this.hasApPermission,
          'Petty Cash': this.hasPcPermission,
          'Payroll': this.hasPrPermission,
          'Journal Entry': this.hasJePermission
        }
        return transactionTypes[type]
      },
      // parent/batch level checkbox selectable or not
      batchSelectable (row) {
        return this.transactions.filter(trx => trx.batch === row.id).some((trx) => {
          return this.transactionPermissions(trx)
        })
      }
    },
    computed: {
      ...mapGetters({
        instance: 'getActiveInstance',
        selectedTransactions: 'getSelectedTransactions',
        getBankByBatchId: 'getBankByBatchId',
        getBankById: 'getBankById',
        reportErr: 'getReportError',
        getVendorById: 'getVendorById',
        checkIfUserHasRequiredPermissions: 'checkIfUserHasRequiredPermissions'
      }),
      /**
       * Returns a boolean indicating whether to disable filters
       * @return Boolean
       */
      shouldDisableFilters () {
        return !(this.filters.search || this.filters.batch__in.length) &&
          !!(!this.transactions.length || !this.dataLoaded || this.filtering || this.auditing || this.posting)
      },
      /**
       * Returns a boolean indicating whether the filters are empty
       */
      filtersAreEmpty () {
        return !this.filters.search && !this.filters.batch__in.length
      },
      /**
       * Returns an array of all postable transactions
       * @return {*[]}
       */
      transactions () {
        let trxs = []
        if (this.$route.query && this.$route.query.type) {
          trxs = this.$store.getters[`get${this.$route.query.type}Cache`]
        } else {
          trxs = [...this.$store.getters.getJesCache, ...this.$store.getters.getApsCache, ...this.$store.getters.getPcsCache]
        }
        return trxs
          .filter(trx => trx.status === 'OPEN' || trx.status === 'AUDITED')
          .sort((a, b) => a.ledger > b.ledger)
      },
      /**
       * Returns an array of all batches associated with all transactions
       * @return Array
       */
      batches () {
        const { getBatchById } = this.$store.getters
        return [...new Set(this.transactions.map(trx => trx.batch))]
          .sort((a, b) => b - a)
          .map(id => getBatchById(id))
      },
      /**
       * Returns a slice of 20 batches
       * @return Array
       */
      batchPageData () {
        return this.batches.slice((this.currentPage - 1) * 20, this.currentPage * 20)
      },
      /**
       * Returns all transactions associated with the current page of batches
       * @return Array
       */
      trxPageData () {
        const viewedBatches = this.batchPageData.map(batch => batch.id)
        return this.transactions.filter(trx => viewedBatches.includes(trx.batch))
      },
      /**
       * Returns an object that shows which batch is currently "expanded" and the transactions that are associated
       * with that batch.
       * @return Object
       */
      expandedBatchTransactions () {
        let data = {}
        this.expandedBatches.forEach(batchId => {
          data[batchId] = this.transactions.filter(trx => trx.batch === batchId)
        })
        return data
      },
      /**
       * Returns a boolean indicating whether a report is being received
       * @return {*}
       */
      receivingReports () {
        const incoming = this.$store.getters.getReportFetchStatus
        return (incoming && incoming.status)
      },
      /**
       * Returns a boolean indicating whether the filters have been changed
       * @return {boolean}
       */
      filtersHaveChanged () {
        return this.previousFilters !== JSON.stringify(this.filters)
      },
      /**
       * Returns an array of batch IDs corresponding to the current transaction selection
       * @return Array
       */
      selectedTrxBatches () {
        return this.selectedTrxs
          .filter(ledgerId => !!this.transactions.find(trx => trx.ledger === ledgerId))
          .map(ledgerId => this.transactions.find(trx => trx.ledger === ledgerId).batch)
      },
      /**
       * Returns the page title
       */
      pageTitle () {
        const baseName = 'TRANSACTION POSTING'
        if (!(this.$route.query && this.$route.query.type)) {
          return baseName
        }
        switch (this.$route.query.type) {
          case 'Aps':
            return baseName + ': ACCOUNTS PAYABLE'
          case 'Jes':
            return baseName + ': JOURNAL ENTRIES'
          case 'Pcs':
            return baseName + ': PETTY CASH'
          default:
            return baseName
        }
      },
      hasApPermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_accountspayable'])
      },
      hasJePermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_journalentry'])
      },
      hasPrPermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_payrollentry'])
      },
      hasPcPermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_pettycash'])
      }
    },
    watch: {
      /**
       * Watches the value of this.currentPage. If it changes, calls the restoreSelection method.
       */
      currentPage () {
        this.restoreSelection()
      },
      /**
       * Watches the current set of transactions. If it changes, checks whether a selected transaction is still in
       * the transaction data set. If not, removes it from the array of selected transaction ledger IDs.
       */
      transactions () {
        if (!this.filtering) {
          const currentTransactionLedgerIds = this.transactions.map(trx => trx.ledger)
          this.selectedTrxs.forEach(selectedTrx => {
            if (!currentTransactionLedgerIds.includes(selectedTrx)) {
              this.selectedTrxs.splice(this.selectedTrxs.indexOf(selectedTrx, 1))
            }
          })
        }
      },
      /**
       * Watches the current set of batches. If it changes, checks whether a batch is still in the batch data set.
       * If not, removes it from this.originalBatchs (used by the batch filter).
       */
      batches () {
        const originalBatchIds = this.originalBatches.map(batch => batch.id)
        this.batches.forEach(batch => {
          if (!originalBatchIds.includes(batch.id)) {
            this.originalBatches.splice(originalBatchIds.indexOf(batch.id), 1)
          }
        })
      },
      /**
       * Watches for changes in the route URL's "type" query string param. This is needed in order to have nav menu "Audit/Post"
       * entries, because the easiest way to differentiate whether a user landed directly on the Transaction Posting program
       * versus if they got there by clicking "Audit/Post" in the nav menu under a specific transaction module is to use
       * query string parameters. However, since no route navigation is taking place (because it's the same component),
       * then the only way to trigger an action that updates the component when a query string parameter is added/changed is to
       * watch the $route object's query.type sub-property. This way, if a "type" query string parameter is added/changed,
       * then this.loadData will be called, updating the component to display OPEN/AUDITED transactions of type 'this.$route.type'.
       */
      '$route.query.type' () {
        if (this.$route.query && this.$route.query.type) {
          this.reloadComponent()
        }
      }
    },
    async created () {
      await this.loadData()
      this.resetComponentState()
    },
    mounted () {
      this.bindMousetrap()
      this.$root.$on('reloadTransactionPostingData', async () => await this.reloadComponent())  // Reload data when instance changes
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('trans_posting').offsetHeight
          const headerHeight = document.getElementById('trans_posting-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
    },
    beforeDestroy () {
      this.$store.dispatch('updateTransactionSelection', [])
    },
    async beforeRouteLeave (to, from, next) {
      await this.clearTransactionsFromStore()
      next()
    }
  }
</script>

<style scoped>
  .audited-row {
    background: lightgoldenrodyellow;
  }
  .clickable-trx-row {
    cursor: pointer;
    color: blue;
  }
  #trans_posting-header {
    border-bottom: 1px solid lightgray;
  }
  /*Input form table style properties*/
  table {
    border-spacing: 0;
    border: 1px solid #bfcbd9;
    background-color: #efefef;
  }
  /*Table style properties*/
  .h-padded {
    padding: 0 12px;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  .trx-action-buttons {
    text-align: center;
    width: 170px;
  }
  .trx-cell-data {
    margin: 0 18px;
    overflow-wrap: break-word;
    white-space: normal;
  }
  .header-centered {
    text-align: center;
  }
  .trx-batch-table-header {
    width: 100%;
    border-top: 1px solid #dfe6ec;
    border-left: 1px solid #dfe6ec;
    border-right: 1px solid #dfe6ec;
    padding: 6px 12px;
    background: #eef1f6;
    font-size: 14px;
    color: #1f2d3d;
    font-weight: 600;
  }
</style>



// WEBPACK FOOTER //
// src/components/Programs/TransactionPosting.vue